<div class="bg-white dark:bg-gray-800 shadow-lg rounded-2xl overflow-hidden">

  <!-- ── Header ── -->
  <div class="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-5">
    <h2 class="text-xl font-bold text-white">{{ toCapitilize(groupName()!) || 'Group Members' }}</h2>
    <p class="text-indigo-100 text-sm mt-1">
      {{ users()?.length || 0 }} member{{ (users()?.length || 0) !== 1 ? 's' : '' }} &middot; Manage who has access to this group
    </p>
  </div>

  <div class="p-6 space-y-6">

    <!-- ── Section 1: Add Member ── -->
    <div>
      <div class="flex items-center gap-2 mb-3">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-indigo-500" viewBox="0 0 20 20" fill="currentColor">
          <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
        </svg>
        <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">Add Member</h3>
      </div>

      <!-- Add Member trigger button -->
      <div *ngIf="!showAddUser()">
        <button
          (click)="toggleAddUser()"
          class="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-semibold rounded-xl border border-indigo-200 dark:border-indigo-700 transition-all duration-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
          </svg>
          Add Member by Email
        </button>
      </div>

      <!-- Search panel -->
      <div *ngIf="showAddUser()" class="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-2xl p-4 space-y-3">
        <label class="text-sm font-semibold text-indigo-700 dark:text-indigo-300 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          Search user by email
        </label>
        <div class="flex gap-2">
          <input
            type="email"
            [ngModel]="searchEmail()"
            (ngModelChange)="onSearchChange($event)"
            placeholder="Enter email address…"
            class="flex-1 px-4 py-2.5 bg-white dark:bg-gray-800 border border-indigo-200 dark:border-indigo-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm shadow-sm"
          />
          <button
            (click)="toggleAddUser()"
            class="px-4 py-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl border border-gray-300 dark:border-gray-600 text-sm transition-colors"
          >
            Cancel
          </button>
        </div>

        <!-- Matched User Result -->
        <div *ngIf="matchedUser() && searchEmail()" class="p-3 bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 rounded-xl flex items-center justify-between shadow-sm">
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center flex-shrink-0">
              <span class="text-green-700 dark:text-green-300 font-bold text-sm">
                {{ (matchedUser()?.displayName || matchedUser()?.email || 'U').charAt(0).toUpperCase() }}
              </span>
            </div>
            <div>
              <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ matchedUser()?.displayName || 'User' }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">{{ matchedUser()?.email }}</div>
            </div>
          </div>
          <button
            (click)="addMatchedUser()"
            class="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
          >
            Add
          </button>
        </div>

        <!-- No match indicator -->
        <div *ngIf="searchEmail() && !matchedUser()" class="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 italic pl-1">
          <svg class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
          Searching…
        </div>
      </div>
    </div>

    <!-- ── Pending Additions Banner ── -->
    <div *ngIf="usersToAdd.length > 0" class="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-2xl p-4">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <svg class="h-4 w-4 text-amber-600 dark:text-amber-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
          </svg>
          <span class="text-sm font-semibold text-amber-700 dark:text-amber-300">
            {{ usersToAdd.length }} pending addition{{ usersToAdd.length !== 1 ? 's' : '' }}
          </span>
        </div>
        <button
          (click)="onSaveUsers()"
          class="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
        >
          Save Changes
        </button>
      </div>
      <ul class="space-y-1.5">
        <li *ngFor="let user of usersToAdd" class="flex items-center justify-between py-2 px-3 bg-white dark:bg-gray-800 rounded-xl">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
              <span class="text-blue-700 dark:text-blue-300 font-bold text-xs">
                {{ (user.displayName || user.email || 'U').charAt(0).toUpperCase() }}
              </span>
            </div>
            <div>
              <div class="text-sm font-medium text-gray-800 dark:text-gray-100">{{ user.displayName || 'User' }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">{{ user.email }}</div>
            </div>
          </div>
          <button
            (click)="removeFromAddList(user.email)"
            class="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
            aria-label="Remove from pending"
          >
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </li>
      </ul>
    </div>

    <!-- ── Divider ── -->
    <div class="border-t border-gray-100 dark:border-gray-700"></div>

    <!-- ── Section 2: Current Members ── -->
    <div>
      <div class="flex items-center gap-2 mb-4">
        <svg class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">Current Members</h3>
        <span class="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full font-medium">
          {{ users()?.length || 0 }}
        </span>
      </div>

      <!-- Empty state -->
      <div *ngIf="!users()?.length" class="text-center py-10">
        <div class="w-14 h-14 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center">
          <svg class="h-7 w-7 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <p class="text-sm font-medium text-gray-500 dark:text-gray-400">No members yet</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Use "Add Member by Email" above to add people.</p>
      </div>

      <!-- Member list -->
      <ul *ngIf="users()?.length" class="divide-y divide-gray-100 dark:divide-gray-700/50">
        <li *ngFor="let user of users()" class="member-row flex items-center justify-between py-3 group/item">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center flex-shrink-0">
              <span class="text-indigo-700 dark:text-indigo-300 font-bold">
                {{ (user.displayName || user.email || 'U').charAt(0).toUpperCase() }}
              </span>
            </div>
            <div>
              <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ user.displayName || 'User' }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">{{ user.email }}</div>
            </div>
          </div>
          <button
            (click)="onRemoveUser(user)"
            class="p-2 text-transparent group-hover/item:text-gray-300 hover:!text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-all duration-200"
            aria-label="Remove user from group"
            title="Remove from group"
          >
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6"/>
            </svg>
          </button>
        </li>
      </ul>
    </div>

  </div>
</div>
